import { PaymentChannelType, ChannelValue } from '@/services/types';

// API 相关常量
export const API_CONSTANTS = {
  // 基础配置
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
  TIMEOUT: 10000, // 10秒
  
  // 端点
  ENDPOINTS: {
    RECHARGE_CONFIGS: '/recharge_configs',
    GENERATE_LINK: '/recharge_config/generate_link'
  },
  
  // 重试配置
  RETRY: {
    MAX_RETRIES: 3,
    INITIAL_DELAY: 1000, // 1秒
    BACKOFF_MULTIPLIER: 2
  },
  
  // 请求头
  HEADERS: {
    CONTENT_TYPE: 'application/json',
    ACCEPT: 'application/json'
  }
} as const;

// 支付渠道常量
export const PAYMENT_CHANNELS = {
  // 支付类型
  TYPES: {
    WECHAT: 'wechat' as PaymentChannelType,
    ALIPAY: 'alipay' as PaymentChannelType,
    VOUCHER: 'voucher' as PaymentChannelType,
    STAR: 'star' as PaymentChannelType,
    USDT: 'usdt' as PaymentChannelType
  },
  
  // 渠道值
  VALUES: {
    TMPAY: 'TMPAY' as ChannelValue,
    SJZF: 'SJZF' as ChannelValue,
    DEFAULT: 'DEFAULT' as ChannelValue,
    CLOSED: 'CLOSED' as ChannelValue
  },
  
  // 渠道名称映射
  NAMES: {
    TMPAY: '同盟支付',
    SJZF: '速捷支付',
    DEFAULT: '默认',
    CLOSED: '关闭'
  },
  
  // 支付类型名称映射
  TYPE_NAMES: {
    wechat: '微信支付',
    alipay: '支付宝',
    voucher: '代金券',
    star: '星币',
    usdt: 'USDT'
  },
  
  // 支付类型图标
  TYPE_ICONS: {
    wechat: '💬',
    alipay: '🅰️',
    voucher: '🎫',
    star: '⭐',
    usdt: '💰'
  }
} as const;

// 缓存相关常量
export const CACHE_CONSTANTS = {
  // 缓存键
  KEYS: {
    RECHARGE_CONFIG: 'recharge_config_cache',
    LAST_SELECTED_CHANNEL: 'last_selected_channel',
    USER_PREFERENCES: 'user_preferences',
    DEBUG_SETTINGS: 'debug_settings'
  },
  
  // 缓存时间（毫秒）
  TTL: {
    SHORT: 1 * 60 * 1000,      // 1分钟
    MEDIUM: 5 * 60 * 1000,     // 5分钟
    LONG: 30 * 60 * 1000,      // 30分钟
    VERY_LONG: 24 * 60 * 60 * 1000  // 24小时
  },
  
  // 默认缓存时间
  DEFAULT_TTL: 5 * 60 * 1000 // 5分钟
} as const;

// 错误码常量
export const ERROR_CODES = {
  // 成功
  SUCCESS: 0,
  
  // 客户端错误 (1000-1999)
  CLIENT_ERROR: {
    INVALID_PARAMS: 1001,
    MISSING_PARAMS: 1002,
    VALIDATION_FAILED: 1003,
    UNAUTHORIZED: 1004,
    FORBIDDEN: 1005,
    NOT_FOUND: 1006,
    TIMEOUT: 1007,
    NETWORK_ERROR: 1008,
    PARSE_ERROR: 1009
  },
  
  // 服务器错误 (2000-2999)
  SERVER_ERROR: {
    INTERNAL_ERROR: 2001,
    SERVICE_UNAVAILABLE: 2002,
    DATABASE_ERROR: 2003,
    EXTERNAL_API_ERROR: 2004
  },
  
  // 业务错误 (3000-3999)
  BUSINESS_ERROR: {
    INVALID_RECHARGE_ID: 3001,
    CHANNEL_NOT_AVAILABLE: 3002,
    PAYMENT_FAILED: 3003,
    ORDER_EXPIRED: 3004,
    INSUFFICIENT_BALANCE: 3005
  }
} as const;

// 错误消息映射
export const ERROR_MESSAGES = {
  [ERROR_CODES.SUCCESS]: '操作成功',
  
  // 客户端错误
  [ERROR_CODES.CLIENT_ERROR.INVALID_PARAMS]: '参数无效',
  [ERROR_CODES.CLIENT_ERROR.MISSING_PARAMS]: '缺少必要参数',
  [ERROR_CODES.CLIENT_ERROR.VALIDATION_FAILED]: '参数验证失败',
  [ERROR_CODES.CLIENT_ERROR.UNAUTHORIZED]: '未授权访问',
  [ERROR_CODES.CLIENT_ERROR.FORBIDDEN]: '访问被禁止',
  [ERROR_CODES.CLIENT_ERROR.NOT_FOUND]: '资源不存在',
  [ERROR_CODES.CLIENT_ERROR.TIMEOUT]: '请求超时',
  [ERROR_CODES.CLIENT_ERROR.NETWORK_ERROR]: '网络连接失败',
  [ERROR_CODES.CLIENT_ERROR.PARSE_ERROR]: '数据解析失败',
  
  // 服务器错误
  [ERROR_CODES.SERVER_ERROR.INTERNAL_ERROR]: '服务器内部错误',
  [ERROR_CODES.SERVER_ERROR.SERVICE_UNAVAILABLE]: '服务暂不可用',
  [ERROR_CODES.SERVER_ERROR.DATABASE_ERROR]: '数据库错误',
  [ERROR_CODES.SERVER_ERROR.EXTERNAL_API_ERROR]: '外部接口错误',
  
  // 业务错误
  [ERROR_CODES.BUSINESS_ERROR.INVALID_RECHARGE_ID]: '无效的充值产品ID',
  [ERROR_CODES.BUSINESS_ERROR.CHANNEL_NOT_AVAILABLE]: '支付渠道不可用',
  [ERROR_CODES.BUSINESS_ERROR.PAYMENT_FAILED]: '支付失败',
  [ERROR_CODES.BUSINESS_ERROR.ORDER_EXPIRED]: '订单已过期',
  [ERROR_CODES.BUSINESS_ERROR.INSUFFICIENT_BALANCE]: '余额不足',
  
  // 默认错误消息
  DEFAULT: '未知错误'
} as const;

// UI 相关常量
export const UI_CONSTANTS = {
  // 加载状态文本
  LOADING_TEXTS: {
    FETCHING_CONFIG: '正在获取配置...',
    GENERATING_LINK: '正在生成支付链接...',
    PROCESSING: '处理中...',
    LOADING: '加载中...'
  },
  
  // 按钮文本
  BUTTON_TEXTS: {
    GENERATE: '生成支付链接',
    RETRY: '重试',
    REFRESH: '刷新',
    RESET: '重置',
    COPY: '复制',
    COPIED: '已复制'
  },
  
  // 状态文本
  STATUS_TEXTS: {
    SUCCESS: '成功',
    FAILED: '失败',
    PENDING: '等待中',
    EXPIRED: '已过期',
    AVAILABLE: '可用',
    UNAVAILABLE: '不可用'
  },
  
  // 颜色主题
  COLORS: {
    PRIMARY: '#1890ff',
    SUCCESS: '#52c41a',
    WARNING: '#faad14',
    ERROR: '#ff4d4f',
    INFO: '#1890ff',
    DISABLED: '#d9d9d9'
  }
} as const;

// 验证规则常量
export const VALIDATION_RULES = {
  // UUID 正则表达式
  UUID_REGEX: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  
  // 价格正则表达式（支持小数点后两位）
  PRICE_REGEX: /^\d+(\.\d{1,2})?$/,
  
  // 支付渠道值列表
  VALID_CHANNELS: ['TMPAY', 'SJZF', 'DEFAULT', 'CLOSED'] as ChannelValue[],
  
  // 支付类型列表
  VALID_PAYMENT_TYPES: ['wechat', 'alipay', 'voucher', 'star', 'usdt'] as PaymentChannelType[],
  
  // 字符串长度限制
  STRING_LIMITS: {
    MIN_NAME_LENGTH: 1,
    MAX_NAME_LENGTH: 100,
    MAX_DESCRIPTION_LENGTH: 500
  }
} as const;

// 调试相关常量
export const DEBUG_CONSTANTS = {
  // 日志级别
  LOG_LEVELS: {
    ERROR: 0,
    WARN: 1,
    INFO: 2,
    DEBUG: 3
  },
  
  // 调试标志
  FLAGS: {
    ENABLE_API_LOGS: import.meta.env.DEV,
    ENABLE_CACHE_LOGS: import.meta.env.DEV,
    ENABLE_PERFORMANCE_LOGS: import.meta.env.DEV
  },
  
  // 性能监控
  PERFORMANCE: {
    API_SLOW_THRESHOLD: 3000, // 3秒
    RENDER_SLOW_THRESHOLD: 100 // 100毫秒
  }
} as const;

// 环境相关常量
export const ENV_CONSTANTS = {
  // 环境类型
  ENVIRONMENTS: {
    DEVELOPMENT: 'development',
    PRODUCTION: 'production',
    TEST: 'test'
  },
  
  // 当前环境
  CURRENT_ENV: import.meta.env.MODE || 'development',
  
  // 是否为开发环境
  IS_DEV: import.meta.env.DEV,
  
  // 是否为生产环境
  IS_PROD: import.meta.env.PROD,
  
  // 构建时间
  BUILD_TIME: '__BUILD_TIME__' in globalThis ? (globalThis as any).__BUILD_TIME__ : new Date().toISOString()
} as const;

// 导出所有常量的联合类型
export type ApiConstants = typeof API_CONSTANTS;
export type PaymentChannels = typeof PAYMENT_CHANNELS;
export type CacheConstants = typeof CACHE_CONSTANTS;
export type ErrorCodes = typeof ERROR_CODES;
export type ErrorMessages = typeof ERROR_MESSAGES;
export type UiConstants = typeof UI_CONSTANTS;
export type ValidationRules = typeof VALIDATION_RULES;
export type DebugConstants = typeof DEBUG_CONSTANTS;
export type EnvConstants = typeof ENV_CONSTANTS;

// 默认导出所有常量
export default {
  API: API_CONSTANTS,
  PAYMENT: PAYMENT_CHANNELS,
  CACHE: CACHE_CONSTANTS,
  ERROR_CODES,
  ERROR_MESSAGES,
  UI: UI_CONSTANTS,
  VALIDATION: VALIDATION_RULES,
  DEBUG: DEBUG_CONSTANTS,
  ENV: ENV_CONSTANTS
} as const;